import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { DeleteOutlined as DeleteIcon } from "@mui/icons-material";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

import type {
  FileItem,
  FileListCallbacks,
  FileListConfig,
  FileSubmissionEvaluator,
  ProcessingArtifactType,
  UploadedFileType,
  BucketType,
} from "./types";
import {
  formatFileSize,
  defaultFileSubmissionEvaluator,
  getFileItemHoverStyles,
} from "./utils";
import { useFileListState, useFileDisplay } from "./hooks";
import { FileViewerDialog } from "../FileViewerDialog";
import { NotificationSnackbar } from "../../common/NotificationSnackbar";
import { formatJobDate } from "../../../utils/jobUtils";
import {
  ARTIFACT_TYPE_LABELS,
  UPLOADED_FILE_TYPE_LABELS,
  BUCKET_CONFIG,
  BUCKET_LABELS,
  convertGenericToUploadedFile,
} from "./types";
interface FileListProps {
  /** Array of files to display */
  files: FileItem[];

  /** Callback handlers for file operations */
  callbacks: FileListCallbacks;

  /** Configuration options */
  config?: FileListConfig;

  /** Type of files being displayed - determines which type labels to show */
  fileTypeCategory?: "uploaded" | "processing";

  /** Function to evaluate file submission status */
  fileSubmissionEvaluator?: FileSubmissionEvaluator;

  /** Custom renderer for file items */
  renderFileItem?: (
    file: FileItem,
    index: number,
    defaultProps: {
      isDownloading: boolean;
      isDeleting: boolean;
      isSubmitted: boolean;
      showPreview: boolean;
      canDelete: boolean;
      onClick: (e: React.MouseEvent) => void;
    },
  ) => React.ReactNode;

  /** Custom renderer for empty state */
  renderEmptyState?: () => React.ReactNode;

  /** Additional context for delete validation */
  deleteContext?: any;

  /** Function to validate if a file can be deleted */
  canDeleteFile?: (
    file: FileItem,
    context?: any,
  ) => { canDelete: boolean; reason?: string };

  subheader?: React.ReactNode;
}

/**
 * Helper function to get chip configuration (label and styles) based on file and mode
 * Reviewer can update file expected bucket
 * Advisor can update file type
 */
function getChipConfig(file: FileItem, isBucketMode: boolean) {
  if (isBucketMode) {
    const bucketKey = file.expectedBucket as BucketType | undefined;
    const bucketInfo =
      (bucketKey && BUCKET_CONFIG[bucketKey]) ??
      (BUCKET_CONFIG.unsupported || {
        label: "N/A",
        bgcolor: "red",
        color: "white",
      });

    return {
      label: bucketInfo.label,
      sx: {
        bgcolor: bucketInfo.bgcolor,
        color: bucketInfo.color,
        fontWeight: 600,
        fontSize: "0.75rem",
      },
    };
  } else {
    return {
      label: file.typeLabel || "UNTAGGED",
      sx: {
        bgcolor: file.typeLabel ? "grey.200" : "warning.light",
        color: file.typeLabel ? "text.primary" : "warning.dark",
        fontWeight: file.typeLabel ? 400 : 600,
      },
    };
  }
}

export function FileList({
  files,
  callbacks,
  config = {},
  fileTypeCategory = "processing",
  fileSubmissionEvaluator = defaultFileSubmissionEvaluator,
  renderFileItem,
  renderEmptyState,
  deleteContext,
  canDeleteFile,
  subheader,
}: FileListProps) {
  const [typeMenuState, setTypeMenuState] = React.useState<{
    anchorEl: HTMLElement | null;
    file: FileItem | null;
    pendingValue: ProcessingArtifactType | UploadedFileType | BucketType | null;
  }>({ anchorEl: null, file: null, pendingValue: null });

  // Determine if we're in bucket mode or type mode
  const isBucketMode = !!callbacks.onUpdateBucket;

  const handleTypeChipClick = (
    e: React.MouseEvent<HTMLElement>,
    file: FileItem,
  ) => {
    e.stopPropagation();
    setTypeMenuState({
      anchorEl: e.currentTarget,
      file,
      pendingValue: isBucketMode
        ? ((file.expectedBucket as BucketType) ?? null)
        : ((file.type as ProcessingArtifactType | UploadedFileType) ?? null),
    });
  };

  const handleTypeMenuClose = async (
    newValue?: ProcessingArtifactType | UploadedFileType | BucketType,
  ) => {
    const { file } = typeMenuState;
    // Always close the menu
    setTypeMenuState({ anchorEl: null, file: null, pendingValue: null });

    // If a new value was selected, proceed with the update
    if (newValue && file) {
      if (isBucketMode) {
        const prevBucket = file.expectedBucket;
        if (
          prevBucket !== newValue &&
          typeof callbacks.onUpdateBucket === "function"
        ) {
          await callbacks.onUpdateBucket(file, newValue as BucketType);
        }
      } else {
        const prevType = file.type as
          | ProcessingArtifactType
          | UploadedFileType
          | undefined;
        if (
          prevType !== newValue &&
          typeof callbacks.onUpdateFileType === "function"
        ) {
          await callbacks.onUpdateFileType(
            file,
            newValue as ProcessingArtifactType | UploadedFileType,
          );
        }
      }
    }
  };

  // Determine which labels to use based on mode and file category
  const menuLabels = isBucketMode
    ? BUCKET_LABELS
    : fileTypeCategory === "uploaded"
      ? UPLOADED_FILE_TYPE_LABELS
      : ARTIFACT_TYPE_LABELS;

  const {
    showActions = true,
    compact = false,
    maxItems,
    allowDelete = true,
    enableViewer = true,
    emptyMessage = "No files uploaded yet",
    showTypeBadges = false,
    readOnlyTypeBadges = false,
    sx = {},
  } = config;

  const {
    downloadingFiles,
    deletingFiles,
    error,
    setError,
    viewerOpen,
    viewerFileIndex,
    deleteDialogFile,
    handleDownload,
    handleItemClick,
    handleViewerClose,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
  } = useFileListState(callbacks);

  const { displayFiles, hasMore, remainingCount } = useFileDisplay(
    files,
    maxItems,
  );

  // Render function for type/bucket action chip
  const renderActionChip = (file: FileItem) => {
    const chipConfig = getChipConfig(file, isBucketMode);

    // If read-only, render a static chip without dropdown
    if (readOnlyTypeBadges) {
      return (
        <Chip
          label={chipConfig.label}
          size="small"
          variant="filled"
          sx={chipConfig.sx}
        />
      );
    }

    // Otherwise, render interactive chip with dropdown menu
    return (
      <>
        <Chip
          label={
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <span>{chipConfig.label}</span>
              <KeyboardArrowDownOutlinedIcon fontSize="small" />
            </Box>
          }
          size="small"
          variant="filled"
          sx={chipConfig.sx}
          onClick={(e) => handleTypeChipClick(e, file)}
          data-action-button
        />
        <Menu
          anchorEl={typeMenuState.anchorEl}
          open={
            Boolean(typeMenuState.anchorEl) &&
            typeMenuState.file?.id === file.id
          }
          onClose={(event) => {
            const anyEvent = event as any;
            if (anyEvent && typeof anyEvent.stopPropagation === "function") {
              anyEvent.stopPropagation();
            }
            void handleTypeMenuClose();
          }}
          onClick={(e) => e.stopPropagation()}
          data-action-button
        >
          {Object.entries(menuLabels).map(([value, label]) => (
            <MenuItem
              sx={{
                fontSize: "12px",
                lineHeight: "14px",
                fontWeight: 500,
              }}
              key={value}
              selected={typeMenuState.pendingValue === value}
              onClick={(e) => {
                e.stopPropagation();
                void handleTypeMenuClose(
                  value as
                    | ProcessingArtifactType
                    | UploadedFileType
                    | BucketType,
                );
              }}
              data-action-button
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  // Render empty state
  if (!files || files.length === 0) {
    if (renderEmptyState) {
      return <>{renderEmptyState()}</>;
    }

    return (
      <Box
        sx={{
          textAlign: "center",
          py: compact ? 2 : 4,
          color: "text.secondary",
          ...sx,
        }}
      >
        <Typography variant={compact ? "body2" : "body1"}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <List
        subheader={
          subheader && (
            <ListSubheader component="div" id="nested-list-subheader">
              {subheader}
            </ListSubheader>
          )
        }
        dense={compact}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          border: compact ? "none" : "1px solid",
          borderColor: "divider",
        }}
      >
        {displayFiles.map((file, index) => {
          const isDownloading = downloadingFiles.has(file.id);
          const isDeleting = deletingFiles.has(file.id);
          const showPreview = callbacks.onPreview !== undefined || enableViewer;
          const isSubmitted = fileSubmissionEvaluator(file);

          // Determine delete capability
          const deleteValidation = canDeleteFile
            ? canDeleteFile(file, deleteContext)
            : { canDelete: true };
          const canDelete =
            allowDelete && deleteValidation.canDelete && !isDeleting;

          const defaultProps = {
            isDownloading,
            isDeleting,
            isSubmitted,
            showPreview,
            canDelete,
            onClick: (e: React.MouseEvent) => {
              // If the type menu is open, do not trigger preview
              if (typeMenuState.anchorEl) return;
              handleItemClick(e, file, index, showPreview, isDeleting);
            },
          };

          // Use custom renderer if provided
          if (renderFileItem) {
            return (
              <React.Fragment key={file.id}>
                {renderFileItem(file, index, defaultProps)}
                {index < displayFiles.length - 1 && (
                  <Box
                    component="hr"
                    sx={{
                      border: "none",
                      borderTop: 1,
                      borderColor: "divider",
                      m: 0,
                    }}
                  />
                )}
              </React.Fragment>
            );
          }

          // Default file item renderer
          return (
            <ListItem
              key={file.id}
              divider={index < displayFiles.length - 1}
              onClick={defaultProps.onClick}
              secondaryAction={
                showActions ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {showTypeBadges && renderActionChip(file)}
                    {callbacks.onDownload && (
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(file)}
                          disabled={isDownloading || isDeleting}
                          data-action-button
                        >
                          {isDownloading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <FileDownloadOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                    {allowDelete && (
                      <Tooltip
                        title={
                          canDelete
                            ? "Delete file"
                            : deleteValidation.reason || "Cannot delete file"
                        }
                      >
                        <Box
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(file)}
                            disabled={!canDelete}
                            data-action-button
                          >
                            {isDeleting ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                ) : undefined
              }
              sx={{
                py: compact ? 0.25 : 1,
                ...getFileItemHoverStyles(showPreview, isDeleting),
              }}
            >
              <ListItemText
                sx={{
                  mr: showActions ? 2 : 0,
                  overflow: "hidden",
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant={compact ? "body2" : "body1"}
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: isSubmitted ? "success.main" : "text.primary",
                        maxWidth: { xs: "200px", sm: "256px", md: "304px" },
                        display: "block",
                      }}
                      title={file.fileName}
                    >
                      {file.fileName}
                    </Typography>
                    {file.isNew && (
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.675rem",
                          fontWeight: 700,
                          bgcolor: "info.light",
                          color: "info.dark",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box
                    component="span"
                    sx={{ display: "flex", gap: 1, alignItems: "center" }}
                  >
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {formatJobDate(file.uploadedAt)}
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      •
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {formatFileSize(file.fileSize)}
                    </Typography>
                    {file.description && (
                      <>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          •
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {file.description}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {hasMore && (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            +{remainingCount} more files
          </Typography>
        </Box>
      )}

      {/* Error Snackbar */}
      <NotificationSnackbar
        message={error}
        onClose={() => setError(null)}
        severity="error"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogFile}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-file-dialog-title"
        aria-describedby="delete-file-dialog-description"
      >
        <DialogTitle id="delete-file-dialog-title">Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-file-dialog-description">
            Are you sure you want to delete "{deleteDialogFile?.fileName}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      {enableViewer && (
        <FileViewerDialog
          open={viewerOpen}
          files={displayFiles.map(convertGenericToUploadedFile)}
          initialFileIndex={viewerFileIndex}
          onClose={handleViewerClose}
          onDownload={
            callbacks.onDownload
              ? (uploadedFile) => {
                  const file = displayFiles.find(
                    (f) => f.s3Key === uploadedFile.s3Key,
                  );
                  if (file && callbacks.onDownload) {
                    callbacks.onDownload(file);
                  }
                }
              : undefined
          }
          getFileUrl={
            callbacks.getFileUrl
              ? async (uploadedFile) => {
                  const file = displayFiles.find(
                    (f) => f.s3Key === uploadedFile.s3Key,
                  );
                  if (file && callbacks.getFileUrl) {
                    return await callbacks.getFileUrl(file);
                  }
                  throw new Error("File not found or getFileUrl not available");
                }
              : undefined
          }
          title="File Viewer"
        />
      )}
    </Box>
  );
}
